<template>
  <form class="flex flex-col gap-8" @submit.prevent="submitForm">
    <div v-if="isPublic">
      <div class="flex justify-between items-center">
        <div class="flex flex-col">
          <p class="text-white text-base font-normal leading-6">
            Has this vulnerability already been reported to another program?
          </p>
          <p class="text-white text-base font-semibold leading-6">
            If yes, Patchstack will only add it to the database, and not assign
            CVE nor process it in the bounty program.
          </p>
        </div>
        <ReportRadioGroup
          id="vuln"
          v-model="form.vuln"
          name="vuln"
          :options="[
            { id: 'yes', label: 'Yes', name: 'yes' },
            { id: 'no', label: 'No', name: 'no' }
          ]"
        />
      </div>
      <h2 class="text-white text-[24px] font-semibold md:mt-16 mb-8">
        Submitter details
      </h2>
      <fieldset class="flex flex-col gap-8">
        <BaseInput
          id="name"
          v-model="form.name"
          type="text"
          label="Researcher"
          placeholder="Name or nickname that we could use for public disclosure"
          name="name"
        />
        <BaseInput
          id="email"
          v-model="form.email"
          type="email"
          label="E-mail"
          placeholder="We might want to get back in touch"
          name="email"
          :error="errors.email"
        />
        <BaseInput
          id="website"
          v-model="form.website"
          type="text"
          label="Website (optional)"
          placeholder="So we can link back to you"
          name="website"
        />
      </fieldset>
    </div>
    <h2 class="text-white text-2xl font-semibold md:mt-16">Component type</h2>
    <ReportRadioGroup
      id="component-type"
      v-model="form.type"
      :options="components"
      name="type"
    />
    <h2 class="text-white text-2xl font-semibold md:mt-16">
      Component details
    </h2>
    <BaseInput
      id="name"
      v-model="form.comp_name"
      type="text"
      label="Component name"
      placeholder="Original plugin or theme directory name"
      name="name"
      :error="errors.comp_name"
    />
    <BaseInput
      id="slug"
      v-model="form.comp_slug"
      type="text"
      label="Component slug"
      placeholder="Original plugin or theme directory name"
      name="comp_slug"
      :error="errors.comp_slug"
    />

    <BaseInput
      id="link"
      v-model="form.comp_link"
      type="text"
      label="Component link"
      placeholder="Eg. plugin page on wordpress.org repository"
      name="comp_link"
      :error="errors.comp_link"
    />
    <div class="flex flex-col gap-2">
      <label for="vuln-version" class="block text-sm font-medium text-primary">
        Vulnerable version *
      </label>
      <div class="flex flex-col sm:flex-row items-center gap-2">
        <!-- Prefix Dropdown -->
        <Autocomplete
          id="version-prefix"
          v-model="form.version_prefix"
          name="version_prefix"
          label=""
          placeholder="Select prefix"
          :items="prefixList"
          variant="underlined"
          key-name="value"
          class="w-full sm:w-60 h-full lg:h-2 md:h-5 align-middle"
        />

        <!-- Text Input -->
        <BaseInput
          id="vuln-version"
          v-model="form.vuln_version"
          type="text"
          label=""
          placeholder="Component version number or range e.g. <= 5.0.1"
          name="vuln_version"
          class="w-full lg:h-10 md:h-1 align-middle"
          :error="errors.vuln_version"
        />
      </div>
    </div>
    <h2 class="text-white text-2xl font-semibold md:mt-16">
      OWASP 2021: TOP 10
    </h2>
    <Autocomplete
      id="vulnerability-class"
      v-model="form.class"
      name="class"
      label="Vulnerability class"
      placeholder="Select option"
      :items="owaspList"
      variant="underlined"
      key-name="value"
      :error="errors.class"
    />
    <Autocomplete
      id="vulnerability-type"
      v-model="form.select_type"
      name="select_type"
      label="Vulnerability type"
      placeholder="Select option"
      :items="vulnTypes"
      variant="underlined"
      key-name="value"
      :error="errors.select_type"
    />
    <div class="flex flex-col gap-2">
      <h2 class="text-white text-2xl font-semibold md:mt-16">Pre-requisite</h2>
      <p class="text-dark4 font-normal text-md">
        Please select the lowest possible user role needed to recreate the
        vulnerability.
      </p>
    </div>
    <Autocomplete
      id="role"
      v-model="form.role"
      name="role"
      label="Pre-requisite"
      placeholder="Select option"
      :items="userRolesArray"
      variant="underlined"
      key-name="value"
      :error="errors.role"
    />

    <BaseInput
      id="custom-role"
      v-model="form.custom_role"
      type="text"
      label="Custom role (optional)"
      placeholder="Some plugins feature custom roles"
      name="custom_role"
    />
    <h2 class="text-white text-2xl font-semibold md:mt-16">
      Vulnerability details
    </h2>
    <div class="flex flex-col gap-2">
      <BaseTextarea
        id="short-description"
        v-model="form.short_desc"
        label="Short description"
        placeholder="State the problem in one or two sentances"
        name="shortDescription"
        :error="errors.short_desc"
      />
      <p class="font-semibold text-darken">
        Markdown is supported.
        <span class="font-normal text-darken"
          >Tip: end a line with two or more spaces for a line-break, or
          soft-return</span
        >
      </p>
    </div>
    <div class="flex flex-col gap-2">
      <BaseTextarea
        id="reproduce"
        v-model="form.reproduce"
        name="reproduce"
        label="How to reproduce"
        placeholder="Describe the steps necessary to reproduce the vulnerability"
        :error="errors.reproduce"
      />
      <p class="font-semibold text-darken">
        Markdown is supported.
        <span class="font-normal text-darken"
          >Tip: end a line with two or more spaces for a line-break, or
          soft-return</span
        >
      </p>
    </div>
    <div class="flex flex-col gap-2">
      <BaseTextarea
        id="additional-info"
        v-model="form.additional_info"
        label="Additional information (optional)"
        placeholder="Links, notes and everything else insightful"
        name="additional_info"
      />
      <p class="font-semibold text-darken">
        Markdown is supported.
        <span class="font-normal text-darken"
          >Tip: end a line with two or more spaces for a line-break, or
          soft-return</span
        >
      </p>
    </div>
    <div class="flex flex-col gap-2">
      <ReportFileUploader v-model="form.files" />
      <p class="font-normal text-darken">
        You can attach 5 files up to 500MB (source code, component files, video
        proof etc.
      </p>
    </div>
    <div v-if="isPublic" class="flex justify-start items-center gap-2 mt-16">
      <label class="relative">
        <BaseCheckbox
          id="consent"
          :model-value="form.consent"
          label=""
          @update:model-value="updateConsent"
          name="consent"
        />
      </label>
      <p class="text-white text-base">
        I have read the
        <BaseLink
          class="text-primary"
          to="https://patchstack.com/academy/general/submitting-a-report/"
          target="_blank"
          >Submission guidelines</BaseLink
        >
        and accept the
        <BaseLink
          to="https://patchstack.com/terms-and-conditions/"
          target="_blank"
          class="text-primary"
          >Terms of services</BaseLink
        >
        &
        <BaseLink
          to="https://patchstack.com/privacy-policy/"
          target="_blank"
          class="text-primary"
          >Privacy policy</BaseLink
        >.
      </p>
    </div>

    <div class="flex justify-center mt-16 pb-16">
      <BaseButton
        class="bg-primary disabled:bg-dark4 hover:bg-secondary py-4 px-4 transition-all duration-300"
        :disabled="isBtnDisabled"
        :class="{ 'pl-5': submitState.isSubmitting }"
        type="submit"
      >
        <div class="flex justify-between items-center gap-2">
          <BaseSpinner v-if="submitState.isSubmitting" class="w-4 h-4" />
          <div class="text-base text-black">Submit report</div>
        </div>
      </BaseButton>
    </div>
  </form>
</template>

<script setup lang="ts">
  import { saveReport } from '@/services';
  import { useVulnerabilitiesStore } from '@/stores/vulnerabilities/vulnerabilities';
  import type { AuthUser, IReport } from '@/types';
  import {
    componentsArray,
    owaspList,
    prefixList,
    prependHttps,
    userRolesArray,
    validateForm,
    VALIDATION_ERROR,
    vulnerabilityTypes
  } from '@/utils';
  import { computed, reactive, ref } from 'vue';
  import * as yup from 'yup';

  interface Props {
    isPublic?: boolean;
  }

  const emit = defineEmits(['report-created']);
  const props = withDefaults(defineProps<Props>(), {
    isPublic: false
  });

  const vulnTypes = vulnerabilityTypes.map((item) => ({
    value: item.value,
    label: item.label
  }));

  const router = useRouter();
  const { $toast } = useNuxtApp();
  const user: Ref<AuthUser | null> = useSanctumUser();
  const name = computed(() => user.value?.name || '');
  const email = computed(() => user.value?.email || '');
  const timerId = ref<ReturnType<typeof setTimeout> | null>(null);

  const submitState = reactive({
    isSubmitting: false,
    isSubmitted: false,
    hasRecaptcha: false,
    recaptchaToken: '',
    recaptchaWidgetId: null
  });

  const form: IReport = reactive({
    type: 'plugin',
    comp_name: '',
    comp_slug: '',
    comp_link: '',
    vuln_version: '<= ',
    version_prefix: '<=',
    class: '',
    select_type: '',
    role: '',
    custom_role: '',
    short_desc: '',
    reproduce: '',
    additional_info: '',
    files: [],
    vuln: 'no',
    name: props.isPublic ? '' : name,
    email: props.isPublic ? '' : email,
    website: '',
    consent: props.isPublic ? false : true
  });

  const errors: Record<string, string> = reactive({
    comp_name: '',
    comp_slug: '',
    comp_link: '',
    vuln_version: '',
    class: '',
    select_type: '',
    short_desc: '',
    reproduce: '',
    email: '',
    role: ''
  });

  const schema = yup.object({
    comp_name: yup.string().required('Component name is required.'),
    comp_slug: yup
      .string()
      .matches(/^[a-z0-9]+(?:-[a-z0-9]+)*$/, 'Slug must be a valid slug.')
      .required('Slug is required.'),
    comp_link: yup
      .string()
      .matches(
        /^(https?:\/\/)?([\da-z\.-]+)\.([a-z\.]{2,6})([\/\w \.-]*)*\/?$/,
        'The URL must be a valid URL including the http(s):// protocol.'
      )
      .required('Component link is required.'),
    vuln_version: yup.string().required('Vulnerable version is required.'),
    class: yup.string().required('Vulnerability class is required.'),
    select_type: yup.string().required('Vulnerability type is required.'),
    short_desc: yup.string().required('Short description is required.'),
    reproduce: yup.string().required('How to reproduce is required.'),
    email: props.isPublic
      ? yup
          .string()
          .email('Invalid email address.')
          .required('Email is required.')
      : yup.string().email('Invalid email address.')
  });

  const updateConsent = (value: boolean | undefined) => {
    if (value === undefined) return;
    form.consent = value;
  };

  const areRequiredFieldsFilled = computed(() => {
    const requiredFields = [
      !!form.comp_name,
      !!form.comp_slug,
      !!form.comp_link,
      !!form.vuln_version,
      !!form.class,
      !!form.select_type,
      !!form.short_desc,
      !!form.reproduce
    ];

    if (props.isPublic) {
      requiredFields.push(!!form.email);
    }

    return requiredFields.every((field) => field === true);
  });

  const isBtnDisabled = computed(() => {
    const commonConditions = [
      submitState.isSubmitting,
      submitState.isSubmitted,
      !areRequiredFieldsFilled.value
    ];

    return props.isPublic
      ? [...commonConditions, !form.consent].some(Boolean)
      : commonConditions.some(Boolean);
  });

  const createFormData = (formState: IReport) => {
    const formData = new FormData();
    Object.entries(formState).forEach(([key, value]) => {
      if (key === 'files' && Array.isArray(value) && value.length) {
        value.forEach((file) => formData.append('files[]', file));
      } else if (value !== '' && value !== null) {
        formData.append(key, value as string);
      }
    });
    return formData;
  };

  const validateFormData = async () => {
    const { isValid, formErrors } = await validateForm(form, schema);
    if (!isValid) {
      for (const key in formErrors) {
        errors[key] = formErrors[key];
      }
    }
    return isValid;
  };

  const handleApiError = (error: any) => {
    if (error?.response?.status === VALIDATION_ERROR) {
      const _errors = error.response._data.errors || {};
      Object.keys(_errors).forEach((key) => {
        errors[key] = _errors[key].join(' ');
      });
    }
  };

  const resetErrors = () => {
    Object.keys(errors).forEach((key) => {
      errors[key] = '';
    });
  };

  const submitForm = async () => {
    try {
      submitState.isSubmitting = true;
      resetErrors();
      const isValid = await validateFormData();
      if (!isValid) {
        $toast.error('Please check if all required fields are filled.');
        return;
      }

      if (props.isPublic) {
        form.website = prependHttps(form.website) as string;
      }
      form.comp_link = prependHttps(form.comp_link) as string;
      const formData = createFormData(form);
      await saveReport(formData);
      submitState.isSubmitted = true;
      $toast.success('Report created successfully.');
      emit('report-created');

      if (!props.isPublic) {
        const store = useVulnerabilitiesStore();
        store.$reset('in-review');

        timerId.value = setTimeout(
          () => router.push(`/researchers/${user.value?.researcher?.uuid}`),
          2000
        );
      }
    } catch (error) {
      $toast.error('Failed to create report.');
      handleApiError(error);
    } finally {
      submitState.isSubmitting = false;
    }
  };

  const components = computed(() => componentsArray);

  // Watcher for version_prefix changes
  watch(
    () => form.version_prefix,
    (newPrefix) => {
      // Extract just the version number without any operators
      const currentVersion = form.vuln_version
        .replace(/^([<>=]+\s*)?/, '')
        .trim();

      // Format with proper spacing
      form.vuln_version = newPrefix
        ? `${newPrefix} ${currentVersion}`
        : currentVersion;
    }
  );
  onUnmounted(() => {
    if (timerId.value !== null) {
      clearTimeout(timerId.value);
    }
  });
</script>
